'use client';
import { FormEvent, useState, useEffect } from 'react';
import Link from 'next/link';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuTrigger,
} from '@core/ui/primitives/dropdown-menu';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/core/ui/primitives/sidebar';
import { cn } from '@core/helpers/misc/GeneralUtils';
import { LuBuilding2, LuChevronsUpDown, LuPlus } from 'react-icons/lu';
import type { IconType } from 'react-icons';
import { Button } from '@core/ui/primitives/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@core/ui/primitives/dialog';
import { OrganizationGlyph } from './OrganizationGlyph';
const LABEL_TRANSITION =
  'transition-[opacity,transform] duration-200 ease-sidebar-smooth group-data-[collapsed=true]/sidebar-wrapper:opacity-0 group-data-[collapsed=true]/sidebar-wrapper:translate-x-3 group-data-[collapsed=true]/sidebar-wrapper:pointer-events-none';
export interface SidebarOrganizationOption {
  id: string;
  name: string;
  plan?: string;
  icon?: IconType;
  slug?: string;
  logoUrl?: string | null;
}
interface OrganizationSidebarOrgSwitcherProps {
  collapsed: boolean;
  organizations: SidebarOrganizationOption[];
  activeOrganizationId: string | null;
  onSelect: (organizationId: string) => void;
  onCreateOrganization?: (name: string) => Promise<unknown>;
  canCreateOrganization?: boolean;
  organizationLimit?: number;
  ownedOrganizationCount?: number;
}
export function OrganizationSidebarOrgSwitcher({
  collapsed,
  organizations,
  activeOrganizationId,
  onSelect,
  onCreateOrganization,
  canCreateOrganization,
  organizationLimit,
  ownedOrganizationCount,
}: OrganizationSidebarOrgSwitcherProps) {
  const [open, setOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [organizationName, setOrganizationName] = useState('');
  const [creatingOrganization, setCreatingOrganization] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const activeOrganization = organizations.find((org) => org.id === activeOrganizationId) ?? organizations[0];
  const normalizedOrganizationName = organizationName.trim();
  const isCreationAllowed = canCreateOrganization ?? true;
  const createDisabled =
    creatingOrganization || !normalizedOrganizationName || !isCreationAllowed || !onCreateOrganization;
  const handleCreateOrganization = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!onCreateOrganization) {
      return;
    }
    if (!normalizedOrganizationName) {
      setCreateError('Organization name is required.');
      return;
    }
    setCreatingOrganization(true);
    try {
      await onCreateOrganization(normalizedOrganizationName);
      setOrganizationName('');
      setCreateError(null);
      setCreateOpen(false);
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : 'Failed to create organization.');
    } finally {
      setCreatingOrganization(false);
    }
  };
  const handleDialogOpenChange = (nextOpen: boolean) => {
    setCreateOpen(nextOpen);
    if (!nextOpen) {
      setOrganizationName('');
      setCreateError(null);
      setCreatingOrganization(false);
    }
  };
  const reachedLimit = canCreateOrganization === false;
  const limitLabel = organizationLimit ?? Math.max((ownedOrganizationCount ?? 0) + 1, 1);
  const ownedCount = ownedOrganizationCount ?? 0;

  // Listen for external create organization event
  useEffect(() => {
    const handleCreateEvent = () => {
      if (isCreationAllowed) {
        setCreateOpen(true);
      }
    };
    window.addEventListener('organization.create.open', handleCreateEvent);
    return () => window.removeEventListener('organization.create.open', handleCreateEvent);
  }, [isCreationAllowed]);

  return (
    <SidebarMenu className='p-2'>
      <SidebarMenuItem>
        <DropdownMenu open={open} onOpenChange={setOpen}>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              size='lg'
              className='group/sidebar-org overflow-hidden justify-start text-left transition-all duration-300 ease-sidebar-smooth data-[state=open]:bg-muted-foreground/15 group-data-[collapsed=true]/sidebar-wrapper:justify-center group-data-[collapsed=true]/sidebar-wrapper:px-0 focus-visible:ring-0 focus-visible:ring-offset-0'>
              <OrganizationGlyph
                name={activeOrganization?.name ?? 'Organization'}
                logoUrl={activeOrganization?.logoUrl ?? null}
                className='h-8 w-8 rounded-lg'
              />
              <div
                className={cn(
                  'ml-2 grid flex-1 overflow-hidden text-left text-sm leading-tight',
                  LABEL_TRANSITION,
                  'group-data-[collapsed=true]/sidebar-wrapper:ml-0'
                )}>
                <span className='truncate font-medium'>{activeOrganization?.name ?? 'Organization'}</span>
                {activeOrganization?.plan && (
                  <span className='truncate text-xs text-muted-foreground'>{activeOrganization.plan}</span>
                )}
              </div>
              <LuChevronsUpDown
                className={cn(
                  'ml-auto mr-0.5 h-4 w-4 text-muted-foreground transition-[opacity,transform] duration-200 ease-sidebar-smooth',
                  'group-data-[collapsed=true]/sidebar-wrapper:opacity-0 group-data-[collapsed=true]/sidebar-wrapper:-translate-x-2'
                )}
              />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className='w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg'
            align='start'
            side={collapsed ? 'right' : 'bottom'}
            sideOffset={6}>
            <DropdownMenuLabel className='text-xs text-muted-foreground'>Organizations</DropdownMenuLabel>
            {organizations.map((org, index) => {
              return (
                <DropdownMenuItem
                  key={org.id}
                  onSelect={(event) => {
                    event.preventDefault();
                    onSelect(org.id);
                    setOpen(false);
                  }}
                  className='gap-3 p-2'>
                  <OrganizationGlyph name={org.name} logoUrl={org.logoUrl ?? null} className='h-6 w-6 rounded-md' />
                  <span className='flex-1 truncate'>{org.name}</span>
                  <DropdownMenuShortcut>⌘{index + 1}</DropdownMenuShortcut>
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              aria-disabled={!isCreationAllowed}
              className={cn(
                'gap-3 p-2 text-muted-foreground hover:!bg-transparent hover:text-primary dark:hover:!bg-transparent dark:hover:text-primary',
                !isCreationAllowed && 'cursor-not-allowed opacity-60 hover:text-muted-foreground'
              )}
              onSelect={(event) => {
                event.preventDefault();
                if (!isCreationAllowed) {
                  return;
                }
                setOpen(false);
                setCreateOpen(true);
              }}>
              <div className='flex h-6 w-6 items-center justify-center rounded-md border border-dashed border-gray-300 bg-transparent dark:border-gray-700'>
                <LuPlus className='h-4 w-4' />
              </div>
              <span className='flex-1 font-medium'>Add organization</span>
              {reachedLimit && <DropdownMenuShortcut className='text-amber-600'>Limit</DropdownMenuShortcut>}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>

      <Dialog open={createOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className='sm:max-w-md'>
          <DialogHeader>
            <DialogTitle>Create organization</DialogTitle>
            <DialogDescription>
              Organizations group vaults and members. You can create up to {limitLabel} organizations under your
              ownership.
            </DialogDescription>
          </DialogHeader>
          {!isCreationAllowed && (
            <div className='rounded-md border border-amber-200/80 bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:border-amber-400/30 dark:bg-amber-900/30 dark:text-amber-100'>
              You have reached the maximum number of organizations for your current plan. Remove an existing
              organization or upgrade to create more.
            </div>
          )}
          <form className='space-y-4 pt-2' onSubmit={handleCreateOrganization}>
            <div className='space-y-2'>
              <label className='text-sm font-medium text-gray-900 dark:text-gray-100' htmlFor='organization-name'>
                Organization name
              </label>
              <input
                id='organization-name'
                type='text'
                autoFocus
                placeholder='e.g., Acme Labs'
                value={organizationName}
                onChange={(event) => {
                  const nextValue = event.target.value;
                  setOrganizationName(nextValue);
                  if (createError) {
                    setCreateError(null);
                  }
                }}
                disabled={creatingOrganization || !isCreationAllowed}
                className='h-9 w-full rounded-md border border-gray-200 px-3 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary dark:border-gray-700 dark:bg-gray-950'
              />
            </div>
            <div className='text-xs text-muted-foreground'>
              {ownedCount > 0
                ? `You currently own ${ownedCount} organization${ownedCount === 1 ? '' : 's'}.`
                : 'You do not own any organizations yet.'}
            </div>
            {createError && <div className='text-sm text-rose-600'>{createError}</div>}
            <DialogFooter className='pt-2'>
              <Button
                type='button'
                variant='ghost'
                onClick={() => handleDialogOpenChange(false)}
                disabled={creatingOrganization}>
                Cancel
              </Button>
              {!isCreationAllowed ? (
                <Button asChild>
                  <Link href='/welcome#pricing'>Upgrade</Link>
                </Button>
              ) : (
                <Button type='submit' disabled={createDisabled}>
                  {creatingOrganization ? 'Creating…' : 'Create'}
                </Button>
              )}
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </SidebarMenu>
  );
}
